/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import PropTypes from 'prop-types';
import truncate from 'lodash/truncate';
import cx from 'classnames';

import {
    Heading,
    useWindowSize,
    FauxImage,
    FauxUnavailableImage,
    PlaceholderText,
    ExternalLink,
} from 'sarsaparilla';

export default function ContestCard({
    className,
    href,
    imagePath,
    mobileImagePath,
    imageAlt,
    imageHeight,
    heading,
    showHeadingOnMobile,
    headingLevel,
    maxHeadingLength,
    copy,
    maxCopyLength,
    locations,
    maxLocationLength,
    hasMinimalLayoutForMobile,
    hasBorder,
    inventoryTags,
    categoryTags,
    children,
    isLoading,
    winnerTags,
    isStaffEntry,
    isExternalLink,
    author,
    submissionDate,
    ...rest
}) {
    const { width } = useWindowSize();
    // Add padding to the promo if it doesn't have a border
    const doesNotHaveBorder = hasBorder ? '' : 'include-padding';
    const includeBorder = hasBorder ? 'has-a-border' : '';

    // external and internal links for cards
    const promoLinkAnchorRouting = isExternalLink ? (
        <ExternalLink
            url={href}
            target="_blank"
            rel="noopener noreferrer"
            className="ugc-content-card-link-anchor"
            {...rest}
        >
            {truncate(heading, { length: maxHeadingLength })}
        </ExternalLink>
    ) : (
        <a
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            className="ugc-content-card-link-anchor"
            {...rest}
        >
            {truncate(heading, { length: maxHeadingLength })}
        </a>
    );

    const promoLinkBody = (
        <div className="ugc-contest-card-link-inner-wrap">
            <div className="ugc-contest-card-link-image-wrap">
                {winnerTags && (
                    <div className="ugc-winner-tags">
                        {winnerTags.map((winnerTag, item) => {
                            const isGrandPrize =
                                winnerTag?.label?.toLowerCase().indexOf('grand prize') >=
                                0;

                            const isCategoryPrize =
                                winnerTag?.label?.toLowerCase().indexOf('category') >= 0;

                            const isMonthlyPrize =
                                winnerTag?.label
                                    ?.toLowerCase()
                                    .includes('january winner') ||
                                winnerTag?.label
                                    ?.toLowerCase()
                                    .includes('february winner') ||
                                winnerTag?.label
                                    ?.toLowerCase()
                                    .includes('march winner') ||
                                winnerTag?.label
                                    ?.toLowerCase()
                                    .includes('april winner') ||
                                winnerTag?.label?.toLowerCase().includes('may winner') ||
                                winnerTag?.label?.toLowerCase().includes('june winner') ||
                                winnerTag?.label?.toLowerCase().includes('july winner') ||
                                winnerTag?.label
                                    ?.toLowerCase()
                                    .includes('august winner') ||
                                winnerTag?.label
                                    ?.toLowerCase()
                                    .includes('september winner') ||
                                winnerTag?.label
                                    ?.toLowerCase()
                                    .includes('october winner') ||
                                winnerTag?.label
                                    ?.toLowerCase()
                                    .includes('november winner') ||
                                winnerTag?.label
                                    ?.toLowerCase()
                                    .includes('december winner');

                            const isMonthlyTopTen =
                                winnerTag?.label
                                    ?.toLowerCase()
                                    .includes('january top ten') ||
                                winnerTag?.label
                                    ?.toLowerCase()
                                    .includes('february top ten') ||
                                winnerTag?.label
                                    ?.toLowerCase()
                                    .includes('march top ten') ||
                                winnerTag?.label
                                    ?.toLowerCase()
                                    .includes('april top ten') ||
                                winnerTag?.label?.toLowerCase().includes('may top ten') ||
                                winnerTag?.label
                                    ?.toLowerCase()
                                    .includes('june top ten') ||
                                winnerTag?.label
                                    ?.toLowerCase()
                                    .includes('april top ten') ||
                                winnerTag?.label
                                    ?.toLowerCase()
                                    .includes('july top ten') ||
                                winnerTag?.label
                                    ?.toLowerCase()
                                    .includes('august top ten') ||
                                winnerTag?.label
                                    ?.toLowerCase()
                                    .includes('september top ten') ||
                                winnerTag?.label
                                    ?.toLowerCase()
                                    .includes('october top ten') ||
                                winnerTag?.label
                                    ?.toLowerCase()
                                    .includes('november top ten') ||
                                winnerTag?.label
                                    ?.toLowerCase()
                                    .includes('december top ten');
                            const isOther =
                                !isGrandPrize && !isCategoryPrize && !isMonthlyPrize;
                            const classes = cx(className, {
                                'ugc-grand-prize-tag': isGrandPrize,
                                'ugc-category-prize-tag': isCategoryPrize,
                                'ugc-monthly-winner-tag': isMonthlyPrize,
                                'ugc-top-ten-tag': isMonthlyTopTen,
                                'ugc-honorable-mention-tag': isOther,
                            });
                            return (
                                <span key={item} className={classes}>
                                    {submissionDate.substr(-4)} {winnerTag?.label}
                                </span>
                            );
                        })}
                    </div>
                )}

                {isStaffEntry && <div className="ugc-staff-tag">Staff Story</div>}

                {imagePath === undefined ? (
                    <FauxUnavailableImage style={{ minHeight: `${imageHeight}` }} />
                ) : (
                    <FauxImage
                        src={imagePath}
                        altText={imageAlt}
                        style={{ minHeight: `${imageHeight}` }}
                    />
                )}
            </div>

            <div className={`ugc-contest-card-link-content-wrap ${includeBorder}`}>
                {categoryTags.length > 0 && (
                    <ul className="ugc-contest-card-category-tags">
                        {categoryTags.map((categoryTag, item) => {
                            return <li key={item}>{categoryTag}</li>;
                        })}
                    </ul>
                )}

                {locations &&
                    locations.length >= 1 &&
                    (isLoading ? (
                        <PlaceholderText fontSize={11} lines={1} />
                    ) : (
                        <ul className="ugc-contest-card-location-tags">
                            {locations.length >= 2 ? (
                                <>
                                    {locations.slice(0, 1).map((location, item) => {
                                        return <li key={item}>{location}</li>;
                                    })}
                                    <li className="ugc-contest-card-location-tags-more">
                                        + {locations.length - 1}{' '}
                                        {locations.length - 1 > 1
                                            ? 'locations'
                                            : 'location'}
                                    </li>
                                </>
                            ) : (
                                <>
                                    {locations.map((location, item) => {
                                        return <li key={item}>{location}</li>;
                                    })}
                                </>
                            )}
                        </ul>
                    ))}

                <div className="ugc-contest-card-link-text-wrap">
                    {isLoading ? (
                        <PlaceholderText fontSize={16} lines={2} />
                    ) : (
                        <Heading headingLevel={headingLevel} appearance="h3">
                            {promoLinkAnchorRouting}
                        </Heading>
                    )}

                    {copy &&
                        copy.length > 1 &&
                        (isLoading ? (
                            <PlaceholderText fontSize={14} lines={4} />
                        ) : (
                            <p>{truncate(copy, { length: maxCopyLength })}</p>
                        ))}
                </div>

                {isLoading ? (
                    <PlaceholderText fontSize={11} lines={1} />
                ) : (
                    <ul className="ugc-contest-card-author-date">
                        <li>By: {author}</li>
                        <li>{submissionDate}</li>
                    </ul>
                )}

                {children}
            </div>
        </div>
    );

    // Show a version of the promo with a minimal layout for mobile
    const promoLinkBodyMobile = (
        <div className="ugc-contest-card-minimal-mobile-layout-wrap">
            {showHeadingOnMobile &&
                (isLoading ? (
                    <PlaceholderText fontSize={16} lines={2} />
                ) : (
                    <div className="ugc-contest-card-minimal-mobile-heading-wrap">
                        <Heading headingLevel={headingLevel} appearance="h3">
                            {promoLinkAnchorRouting}
                        </Heading>
                    </div>
                ))}

            {imagePath === undefined ? (
                <FauxUnavailableImage style={{ minHeight: '90px' }} />
            ) : (
                <FauxImage
                    src={imagePath}
                    mobileSrc={mobileImagePath}
                    altText={imageAlt}
                    style={{ minHeight: '90px' }}
                />
            )}
        </div>
    );

    return (
        <>
            {/* If the width of the viewport is less than 768px and 'hasMinimalLayoutForMobile' */}
            {width <= 768 && hasMinimalLayoutForMobile ? (
                <div
                    className={cx(
                        'ugc-contest-card-link-wrap',
                        doesNotHaveBorder,
                        className
                    )}
                >
                    {promoLinkBodyMobile}
                </div>
            ) : (
                <div
                    className={cx(
                        'ugc-contest-card-link-wrap',
                        doesNotHaveBorder,
                        className
                    )}
                >
                    {promoLinkBody}
                </div>
            )}
        </>
    );
}

ContestCard.propTypes = {
    /** Path for the promo image.   */
    imagePath: PropTypes.string,

    /** Path for the mobile version of the promo image.   */
    mobileImagePath: PropTypes.string,

    /** href for the promo.   */
    href: PropTypes.string.isRequired,

    /** Heading for the promo.   */
    heading: PropTypes.string.isRequired,

    /** <h#> for the promo.   */
    headingLevel: PropTypes.number,

    /** Hide the heading on mobile (use this if the background image contains a graphic)*/
    showHeadingOnMobile: PropTypes.bool,

    /** Alt text for the promo image.   */
    imageAlt: PropTypes.string,

    /** Height of the promo image.   */
    imageHeight: PropTypes.string,

    /** Set a character count for the promo heading.   */
    maxHeadingLength: PropTypes.number,

    /** Copy for the promo.   */
    copy: PropTypes.string,

    /** Set a character count for the promo copy.   */
    maxCopyLength: PropTypes.number,

    /** Location Type.   */
    locations: PropTypes.array,

    /** Set a character count for the location type.   */
    maxLocationLength: PropTypes.number,

    /** Reduce the size of the promo, and only show the image and title.   */
    hasMinimalLayoutForMobile: PropTypes.bool,

    /** Does the promo include Winner Tag?   */
    winnerTags: PropTypes.array,

    /** Additional className that can be added to the component wrapper.   */
    className: PropTypes.string,

    /** Add a border around the content of the promo.   */
    hasBorder: PropTypes.bool,

    /** Define the inventory tags associated with this promo.   */
    inventoryTags: PropTypes.array,

    /** Define the category tags associated with this promo.   */
    categoryTags: PropTypes.array,

    /** Display the Placeholder or PlaceholderText component until the item is loaded   */
    isLoading: PropTypes.bool,

    /** Add additional content to the component if necessary.  */
    children: PropTypes.node,

    /** Is the link for the promo pointing to an external source  */
    isExternalLink: PropTypes.bool,

    noticeType: PropTypes.string,

    author: PropTypes.string,

    submissionDate: PropTypes.string,

    isStaffEntry: PropTypes.bool,
};

ContestCard.defaultProps = {
    headingLevel: 3,
    showHeadingOnMobile: true,
    imageHeight: '140px',
    maxHeadingLength: 43,
    maxCopyLength: 140,
    maxLocationLength: 140,
    hasBorder: true,
    categoryTags: [],
    winnerTags: [],
    isExternalLink: false,
    isStaffEntry: false,
};
